// The table. It draws the state that TableState keeps and sends back the one integer the engine
// asks for; it decides nothing about the game itself.
//
// Layout and movement are a port of the Android table, so the two clients are recognisably the
// same game: FeltTableLayout for the felt and the arc of seats around it, SeatView for what a
// seat shows, FeltSurface for the cloth itself, and CardFlightOverlay for every card that moves.
// The local player sits at the bottom, the others spread over the arc above them, and the card
// each of them plays lands in front of their own seat rather than in a shared pile, so it is
// always clear whose card is whose.
//
// Tap a playable card to select it, then the confirm button to play it - the same two step the
// Android table uses, so a mis-tap in a hand of eleven cards is never a played card.

import { el, clear } from './dom.js';
import { Choice } from '../game/table-state.js';
import { cardImageUrl, cardBackUrl, aspectFor, warmDeck } from '../game/card-art.js';

const NormalHandGap = 6;
// At most this much of a card may be covered by the next one. Calibrated on the worst hand the
// games actually deal: thirteen cards of the french deck, the widest, on a 360px phone. The width
// that matters there is 332px, not 360 - #app pads 14px a side - and fitting thirteen 79px cards
// into it needs 0.735, leaving 21px of every card showing. Below that a hand of figures stops
// being readable, so a narrower screen scrolls instead of overlapping further
const MaxHandOverlap = 0.75;

// FeltTableLayout.BOTTOM_TEXT_BAND_DP: the strip of felt along the bottom edge kept for the status
// line, which is written on the cloth rather than under it
const BottomTextBand = 30;
// A seat never touches the rim, and a card never sits flush against one
const SeatRingMargin = 2;
const CardStandoff = 6;
// How round the corners of the ring of seats are, and how finely each one is walked. Roughly the
// radius FeltSurface gives the cloth itself, a tenth of the short side
const SeatRingCorner = 44;
const CornerSteps = 6;
// However tight the table, a played card stays at least this much of its own height out from the
// middle of the trick, on its owner's side of it
const MinCardOffset = 0.35;
// FeltTableLayout.WIDE_RATIO: a felt this much wider than it is tall is a phone on its side, and is
// laid out differently. Decided from the shape the felt actually has, not from the orientation
const WideRatio = 1.5;
// FeltTableLayout.BOTTOM_TEXT_BAND_WIDE_DP: on its side the table cannot spare the full status band
const WideTextBand = 24;

// Timings, all from the Android originals: CardFlightOverlay's flight, the stagger a deal puts
// between two cards, and the pause before the winner scoops the trick up
const FlightMillis = 260;
const DealStaggerMillis = 90;
const CollectDelayMillis = 450;
const CollectMillis = 430;
// From this many opponents on, a seat has to give up its running total to fit around the rim
const CompactSeatsFrom = 5;
// Eight seats times thirteen cards would be a hundred flying copies for one deal: the opponents
// get a short burst instead, which reads as a deal just the same
const MaxDealtBacksPerSeat = 5;

// How big a card is away from the hand. Both are sized by their WIDTH, like hud_card_width and the
// dp(44) of TrickGameFragment, so that every deck keeps its own shape - but the trevisane cards
// are 230x560, barely two fifths as wide as they are tall, and a card 50px wide is 122px tall.
// A ceiling on the HEIGHT therefore has the last word, or the panel and the felt would be carrying
// nothing but two cards. The trump stops about as tall as a napoletana: at 112px a trevisana made
// the panel 130px tall, every pixel of it taken from the felt, and stood bigger than the cards in
// play
const TrumpCardWidth = 50;
const TrumpCardMaxHeight = 84;
// The size a played card always gets: about a seventh of the table wide, never so small it stops
// being a card nor so tall it crowds the seat it stands in front of
const PlayedCardWidthShare = 0.13;
const PlayedCardMinWidth = 32;
const PlayedCardMaxWidth = 52;
const PlayedCardMaxHeightShare = 0.26;
// ...and the size it may grow to, when the table has room for the whole trick at that size
// (fitPlayedCard). A share of the felt is a guess that is wrong both ways: with two opponents it
// left the cards small in the middle of an empty cloth, with seven it piled them on each other
const PlayedCardLargestWidthShare = 0.16;
const PlayedCardLargestWidth = 64;
const PlayedCardLargestHeightShare = 0.34;
// How much of a played card the others may cover between them, and of a seat a card may cover. A
// trick on a real table overlaps too; what matters is that every card still shows most of itself
const MaxTrickOverlap = 0.2;
const MaxSeatCover = 0.08;

// The panel of chips a question puts on the felt (choicePanelPlace): its padding and the space
// between two chips, both as in .choice-panel, how far it keeps from a seat and from the rim, and
// how wide it ever gets
const ChoicePanelPadding = 10;
const ChoiceChipGap = 6;
const ChoicePanelMargin = 6;
const ChoicePanelMaxWidth = 300;
const ChoicePanelStep = 4;
// Each chip is counted a pixel wider than measured, so a row worked out to the last pixel still
// fits once the browser has done its own rounding. A chip picked does not turn bold in the panel
// (.choice-panel .chip.selected) for the same reason: grown wider, it pushed the last chip of its
// row onto the next
const ChipSlack = 1;

// The width a card of this deck ends up with, once both bounds have had their say
function cardWidthFor(deckType, targetWidth, maxHeight) {
  const aspect = aspectFor(deckType);
  return Math.round(Math.min(targetWidth, maxHeight * aspect));
}

// The gap between the cards of a hand, negative when they have to overlap to fit. Pure arithmetic
// and exported so it can be tested: a browser is needed to lay a hand out, not to work out by how
// much thirteen cards have to overlap
export function handGap(availableWidth, cardWidth, cardsCount) {
  if (cardsCount < 2 || availableWidth <= 0 || cardWidth <= 0) {
    return NormalHandGap;
  }
  const gapThatFits = (availableWidth - cardWidth) / (cardsCount - 1) - cardWidth;
  // Never overlap so far that a card stops being recognisable, even if that means overflowing
  const smallest = -Math.round(cardWidth * MaxHandOverlap);
  // Rounded down: a pixel more overlap nobody sees, a pixel short in each of nine gaps is the last
  // card cut off by the edge of the screen
  return Math.max(smallest, Math.min(NormalHandGap, Math.floor(gapThatFits)));
}

// Where everybody sits and where the card each of them played goes, ported from
// FeltTableLayout.onLayout. The local player is the only fixed point - bottom centre, just above
// the status band - and the others spread around the rim of what is left above them.
//
// A played card then lies on the line from the middle of the table out to its owner, so it is
// always clear whose it is. Two things had to be worked out that the Android table simply asserts,
// because on a phone the felt is short and its arithmetic does not survive there:
//
// - the middle the cards gather round is NOT the middle of the arc of seats. The seats are pressed
//   into the top of the felt, since the local player owns the bottom of it, and cards drawn round
//   their middle landed on the seats' own names while the whole lower half of the cloth sat empty.
//   They gather round the free band instead, the one the crest is painted on;
// - how far out each card sits is settled by its neighbours, not only by its owner's seat. Just
//   clear of the seat is where it goes when there is room for that; when there is not, the cards
//   spread until each still shows enough of itself, which is what a trick looks like anyway.
//
// Sizes come in already measured, so this is arithmetic and nothing else and can be checked
// without a browser: `seats`, `local` and `card` are { width, height } boxes
export function tableGeometry({ width, height, seats, local, card, textBand = BottomTextBand }) {
  if (isWideTable(width, height)) {
    return wideTableGeometry({ width, height, seats, local, card });
  }
  const centerX = width / 2;
  let localSeatTop = height - textBand;
  let localAt = null;
  if (local) {
    localAt = { x: centerX, y: localSeatTop - local.height / 2 };
    localSeatTop -= local.height;
  }
  // The arc lives in the space above the local seat, not over the whole table: with eight
  // opponents the seats at the ends would otherwise end up in the local player's lap
  const ringBottom = local ? localSeatTop - 4 : height;
  const ringCenterY = ringBottom / 2;
  const seatsAt = seatRing({ width, ringBottom, centerX, seats, gap: local?.width ?? 0 });

  // Where the trick gathers: the middle of the cloth left bare between the seats and the local
  // player, which is also where FeltSurface paints its crest
  const trickCenter = { x: centerX, y: (ringCenterY + localSeatTop) / 2 };
  return {
    seats: seatsAt,
    local: localAt,
    cards: card ? playedCardRing(seatsAt, seats, card, trickCenter) : [],
    // The local player's card lies at the bottom of the table, right above their own seat
    localCard: card ? { x: centerX, y: localSeatTop - CardStandoff - card.height / 2 } : null,
    trickCenter,
    centerX,
    ringCenterY,
    localSeatTop,
    localColumn: 0,
  };
}

// The largest played card, between `smallest` and `largest` (both widths, which is how a card is
// sized on screen), with which the whole trick still lies on the felt: every place a card can go -
// one per seat and the local player's - inside the cloth, no card more than MaxTrickOverlap covered
// by all the others together, none covering a seat. Every place is checked whether a card is there
// yet or not, so the size depends on the table and not on the trick, and a card never changes size
// while the others are being played. When not even `smallest` fits, `smallest` it is: a crowded
// table is allowed to crowd
export function fitPlayedCard({ width, height, seats, local, aspect, smallest, largest }) {
  const boxFor = (cardWidth) => ({ width: cardWidth, height: Math.round(cardWidth / aspect) });
  for (let cardWidth = Math.floor(largest); cardWidth > smallest; cardWidth -= 1) {
    const card = boxFor(cardWidth);
    const geometry = tableGeometry({ width, height, seats, local, card });
    if (trickFits(geometry, { width, height, seats, local, card })) return card;
  }
  return boxFor(smallest);
}

function trickFits(geometry, { width, height, seats, local, card }) {
  const cards = [...geometry.cards, geometry.localCard]
    .filter(Boolean)
    .map((at) => ({ at, box: card }));
  const people = seats.map((box, index) => ({ at: geometry.seats[index], box }));
  if (local && geometry.local) people.push({ at: geometry.local, box: local });
  const area = card.width * card.height;
  for (const { at } of cards) {
    if (
      at.x - card.width / 2 < 0 ||
      at.x + card.width / 2 > width ||
      at.y - card.height / 2 < 0 ||
      at.y + card.height / 2 > height
    ) {
      return false;
    }
  }
  for (let a = 0; a < cards.length; a += 1) {
    // Everything that lies on this card, added up: one card a fifth over it is fine, a card on
    // its top corner and another on its bottom one hide half of it between them, and each of the
    // two alone looked harmless. Who covers whom depends on the order of play, so every
    // neighbour counts
    let covered = 0;
    for (let b = 0; b < cards.length; b += 1) {
      if (b !== a) covered += sharedArea(cards[a], cards[b]);
    }
    if (covered > area * MaxTrickOverlap) return false;
    for (const person of people) {
      if (sharedArea(cards[a], person) > person.box.width * person.box.height * MaxSeatCover) {
        return false;
      }
    }
  }
  return true;
}

// How much of two boxes, each centred on its `at`, lies on top of the other
function sharedArea(one, other) {
  const across =
    Math.min(one.at.x + one.box.width / 2, other.at.x + other.box.width / 2) -
    Math.max(one.at.x - one.box.width / 2, other.at.x - other.box.width / 2);
  const down =
    Math.min(one.at.y + one.box.height / 2, other.at.y + other.box.height / 2) -
    Math.max(one.at.y - one.box.height / 2, other.at.y - other.box.height / 2);
  return Math.max(0, across) * Math.max(0, down);
}

// Where the chips of a question go - a bet, a call, a draw from the neighbour: on the felt, in the
// cloth the seats leave free. Nobody has a card down while a table declares, calls or draws, so
// the place the trick gathers in is empty, and the band the chips used to keep under the felt for
// the whole game is given back to it.
//
// The panel covers no seat: a seat shows what its player has declared, which is exactly what the
// one declaring wants to read. It is as wide as the free cloth allows, so that a dozen chips take
// two rows rather than five, and when the seats down the sides leave no band that wide it narrows
// into the column between them. `chips` are the measured boxes of the chips, in order; the answer
// is the panel's centre, its width, the height it may take, and whether the chips fit in it. When
// they do not, it is the largest free stretch of cloth, and the panel scrolls
export function choicePanelPlace({ width, height, geometry, seats, local, chips }) {
  const statusBand = isWideTable(width, height) ? WideTextBand : BottomTextBand;
  const top = ChoicePanelMargin;
  const bottom = height - statusBand - ChoicePanelMargin;
  const around = (at, box) => ({
    left: at.x - box.width / 2 - ChoicePanelMargin,
    right: at.x + box.width / 2 + ChoicePanelMargin,
    top: at.y - box.height / 2 - ChoicePanelMargin,
    bottom: at.y + box.height / 2 + ChoicePanelMargin,
  });
  const blocks = seats.map((box, index) => around(geometry.seats[index], box));
  if (local && geometry.local) blocks.push(around(geometry.local, local));

  const { x, y: wanted } = geometry.trickCenter;
  const chipHeight = Math.max(0, ...chips.map((chip) => chip.height));
  const widest = Math.min(ChoicePanelMaxWidth, 2 * (Math.min(x, width - x) - ChoicePanelMargin));
  const narrowest = Math.max(0, ...chips.map((chip) => chip.width)) + 2 * ChoicePanelPadding;
  let fallback = null;
  for (let panelWidth = widest; panelWidth >= narrowest; panelWidth -= ChoicePanelStep) {
    const { rows, longest } = wrapChips(chips, panelWidth - 2 * ChoicePanelPadding);
    const needed = rows * chipHeight + (rows - 1) * ChoiceChipGap + 2 * ChoicePanelPadding;
    // No wider than its chips: two chips in a panel the width of the table is a banner
    const used = longest + 2 * ChoicePanelPadding;
    const left = x - used / 2;
    const right = x + used / 2;
    let place = null;
    for (const [from, to] of freeSpans(top, bottom, blocks, left, right)) {
      if (!fallback || (to - from) * used > fallback.height * fallback.width) {
        fallback = { x, y: (from + to) / 2, width: used, height: to - from, fits: false };
      }
      if (to - from < needed) continue;
      const y = Math.min(Math.max(wanted, from + needed / 2), to - needed / 2);
      if (!place || Math.abs(y - wanted) < Math.abs(place.y - wanted)) {
        place = { x, y, width: used, height: needed, fits: true };
      }
    }
    if (place) return place;
  }
  return (
    fallback ?? {
      x,
      y: wanted,
      width: narrowest,
      height: Math.max(0, bottom - top),
      fits: false,
    }
  );
}

// How many rows the chips take in a panel this wide inside, and how long the longest row is: the
// browser fills a wrapping row the same way, as many as fit and then the next row
function wrapChips(chips, inner) {
  let rows = 0;
  let row = 0;
  let longest = 0;
  for (const { width } of chips) {
    if (rows === 0 || row + ChoiceChipGap + width > inner) {
      rows += 1;
      row = width;
    } else {
      row += ChoiceChipGap + width;
    }
    longest = Math.max(longest, row);
  }
  return { rows, longest };
}

// The stretches of [top, bottom] that no block reaches, looking only at the blocks that overlap
// the columns from `left` to `right`
function freeSpans(top, bottom, blocks, left, right) {
  const taken = blocks
    .filter((block) => block.right > left && block.left < right)
    .map((block) => [Math.max(top, block.top), Math.min(bottom, block.bottom)])
    .filter(([from, to]) => to > from)
    .sort((a, b) => a[0] - b[0]);
  const spans = [];
  let from = top;
  for (const [start, end] of taken) {
    if (start > from) spans.push([from, start]);
    from = Math.max(from, end);
  }
  if (bottom > from) spans.push([from, bottom]);
  return spans;
}

export function isWideTable(width, height) {
  return height > 0 && width > height * WideRatio;
}

// The table on its side, after FeltTableLayout's wideTable. Turning the phone halves the height and
// doubles the width, and the arc that works upright then stacks a seat, its card, the local
// player's card and the local seat one on top of the other in a strip two hundred pixels tall: the
// cards came out sixteen pixels wide. So here
// - the local player takes the bottom left corner, and the column above it for their own card;
// - everybody else sits along the top edge, in playing order from the left, and plays straight
//   down in front of themselves, so a trick reads as a row and nobody's card crosses anyone else;
// - how tall a card can be is what is left of the strip under the seats (wideCardMaxHeight)
function wideTableGeometry({ width, height, seats, local, card }) {
  const localColumn = Math.max(local?.width ?? 0, card?.width ?? 0) + 2 * CardStandoff;
  let localSeatTop = height - SeatRingMargin;
  let localAt = null;
  if (local) {
    localAt = { x: SeatRingMargin + localColumn / 2, y: localSeatTop - local.height / 2 };
    localSeatTop -= local.height;
  }
  const left = SeatRingMargin + localColumn;
  const right = width - SeatRingMargin;
  const tallest = seats.length > 0 ? Math.max(...seats.map((seat) => seat.height)) : 0;
  // Half a step at either end, as on Android, so the row sits centred on what is left of the edge
  const seatsAt = seats.map((unused, index) => ({
    x: left + ((right - left) * (index + 0.5)) / seats.length,
    y: SeatRingMargin + tallest / 2,
  }));
  const stripTop = SeatRingMargin + tallest + CardStandoff;
  const stripBottom = height - WideTextBand;
  return {
    seats: seatsAt,
    local: localAt,
    cards: card ? seatsAt.map(({ x }) => ({ x, y: stripTop + card.height / 2 })) : [],
    localCard: card && localAt
      ? { x: localAt.x, y: localSeatTop - CardStandoff - card.height / 2 }
      : null,
    trickCenter: { x: (left + right) / 2, y: (stripTop + stripBottom) / 2 },
    centerX: width / 2,
    ringCenterY: (SeatRingMargin + stripBottom) / 2,
    localSeatTop,
    // What the status line written along the bottom has to keep clear of
    localColumn,
  };
}

// How tall a played card may be on a table on its side: the strip between the row of seats and the
// status line, and the column over the local seat, whichever is shorter. The upright table works
// with a share of the felt instead, which on a felt this low is a card nobody can make out
export function wideCardMaxHeight({ height, seats, local }) {
  const tallest = seats.length > 0 ? Math.max(...seats.map((seat) => seat.height)) : 0;
  const strip = height - WideTextBand - SeatRingMargin - tallest - 2 * CardStandoff;
  const column = height - 2 * SeatRingMargin - (local?.height ?? 0) - CardStandoff;
  return Math.max(0, Math.min(strip, column));
}

// The opponents in the order they play, starting from whoever plays right after the local player:
// the order seatRing walks the rim in. TrickGameFragment.buildTableIfNeeded seats them the same
// way. Sorting them by seat number instead is only right for whoever sits in seat 0; for anyone
// else the table on screen jumped about, right then left then across, while the cards went round
export function seatOrder(playersCount, myPosition) {
  const order = [];
  for (let step = 1; step < playersCount; step += 1) {
    order.push((myPosition + step) % playersCount);
  }
  return order;
}

// The opponents, spread evenly around the rim of the table. Android walks the seats round by
// ANGLE and stops each one where its radius meets the rim, which on a felt far wider than it is
// tall crowds them all onto the short top edge: on a phone with four opponents two of them
// overlapped outright. Walking the rim itself and spacing them along it puts the same seats in the
// same places on a square table and keeps them apart on every other one.
//
// The rim is a rectangle and not an ellipse for the reason FeltTableLayout gives: on a screen far
// taller than it is wide an ellipse throws away the four corners, and the seats end up bunched at
// mid height with the middle of the cloth empty. The walk starts to the left of the local player
// and comes back round to their right, so the seats follow the order they play in
function seatRing({ width, ringBottom, centerX, seats, gap }) {
  if (seats.length === 0) return [];
  const widest = Math.max(...seats.map((seat) => seat.width));
  const tallest = Math.max(...seats.map((seat) => seat.height));
  const left = Math.min(centerX, widest / 2 + SeatRingMargin);
  const right = Math.max(centerX, width - widest / 2 - SeatRingMargin);
  const top = Math.min(ringBottom / 2, tallest / 2 + SeatRingMargin);
  const bottom = Math.max(ringBottom / 2, ringBottom - tallest / 2 - SeatRingMargin);
  // The stretch of the bottom edge the local player takes up, which nobody else may stand on
  const half = Math.min(centerX - left, gap / 2 + SeatRingMargin * 4);
  // The corners are rounded, like the felt's own. Squared off, the two seats either side of a
  // corner were as far apart along the rim as anybody else and half as far apart on screen, which
  // is how they came to overlap
  const radius = Math.min(SeatRingCorner, (right - left) / 2, (bottom - top) / 2);

  const path = [{ x: centerX - half, y: bottom }];
  const corner = (cx, cy, from) => {
    for (let step = 1; step <= CornerSteps; step += 1) {
      const angle = from + (Math.PI / 2) * (step / CornerSteps);
      path.push({ x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) });
    }
  };
  path.push({ x: left + radius, y: bottom });
  corner(left + radius, bottom - radius, Math.PI / 2);
  path.push({ x: left, y: top + radius });
  corner(left + radius, top + radius, Math.PI);
  path.push({ x: right - radius, y: top });
  corner(right - radius, top + radius, -Math.PI / 2);
  path.push({ x: right, y: bottom - radius });
  corner(right - radius, bottom - radius, 0);
  path.push({ x: centerX + half, y: bottom });

  const walked = [0];
  for (let index = 1; index < path.length; index += 1) {
    walked.push(
      walked[index - 1] +
        Math.hypot(path[index].x - path[index - 1].x, path[index].y - path[index - 1].y),
    );
  }
  const perimeter = walked[walked.length - 1];

  return seats.map((unused, index) => {
    const distance = (perimeter * (index + 1)) / (seats.length + 1);
    let step = 1;
    while (step < walked.length - 1 && walked[step] < distance) step += 1;
    const leg = walked[step] - walked[step - 1];
    const share = leg > 0 ? (distance - walked[step - 1]) / leg : 0;
    return {
      x: path[step - 1].x + (path[step].x - path[step - 1].x) * share,
      y: path[step - 1].y + (path[step].y - path[step - 1].y) * share,
    };
  });
}

// One card per seat, each on the line from the middle of the trick out to its owner, sitting just
// clear of the seat itself so it never covers a name. The floor is what stops a seat too close to
// the middle from sending its card straight through it and out the far side, where it would look
// like somebody else's
function playedCardRing(seatsAt, seatBoxes, card, center) {
  return seatsAt.map((seat, index) => {
    const dx = seat.x - center.x;
    const dy = seat.y - center.y;
    const angle = Math.atan2(dy, dx);
    const distance = Math.max(1, Math.hypot(dx, dy));
    const clearOfSeat = distance - (seatBoxes[index].height / 2 + CardStandoff + card.height / 2);
    const radius = Math.min(
      Math.max(clearOfSeat, card.height * MinCardOffset),
      distance - CardStandoff,
    );
    return { x: center.x + radius * Math.cos(angle), y: center.y + radius * Math.sin(angle) };
  });
}

// SeatView.initials: the two letters the avatar shows
export function initialsOf(name) {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return trimmed.slice(0, 2).toUpperCase();
}

export class TableView {
  #root;
  #banner;
  #matchIndicator;
  #declarationsInfo;
  #callInfo;
  #trumpBlock;
  #deckLeftText;
  #trumpSeed;
  #deckSlot;
  #felt;
  #emblem;
  #seatLayer;
  #cardLayer;
  #flightLayer;
  #description;
  #results;
  #hand;
  #choicePanel;
  #confirmButton;
  #handlers;

  #state = null;
  #selectedHandIndex = -1;
  #selectedOption = -1;

  // The seat widgets, kept alive between renders: a card can only fly to a seat that is still the
  // same node it was a moment ago
  #seatNodes = new Map();
  #localSeatNode = null;
  // The cards on the felt, in the order they were played, mirroring state.trick
  #played = [];
  #playedKeys = [];
  #collected = false;
  #handKey = null;
  #handNodes = [];
  // What the last render knew, so this one can tell what has just happened
  #dealtMatch = -1;
  #handCounts = [];
  #playOrigin = null;
  // How wide a card on the felt is, as the last layout worked it out
  #cardWidth = PlayedCardMinWidth;
  // True while the server replays our history after a reconnection: the table is being rebuilt,
  // and replaying a hand of animations for a game already watched would be nonsense
  #silent = false;
  // Bumped whenever the table is thrown away, so a flight still in the air lands on nothing
  #generation = 0;
  #timers = new Set();
  // Every card still moving, so a table thrown away can stop them where they are
  #flights = new Set();
  #resizeBound = false;

  // handlers: { onRespond, onLeave }
  constructor(handlers) {
    this.#handlers = handlers;
    this.#build();
  }

  get element() {
    return this.#root;
  }

  #build() {
    this.#banner = el('div', { className: 'table-banner', hidden: true });
    this.#matchIndicator = el('span', { className: 'match-indicator' });
    this.#declarationsInfo = el('span', { className: 'declarations-info', hidden: true });
    this.#callInfo = el('span', { className: 'call-info', hidden: true });
    this.#deckLeftText = el('span', { className: 'hud-caption' });
    this.#trumpSeed = el('span', { className: 'pill-felt', hidden: true });
    this.#deckSlot = el('div', { className: 'deck-slot' });
    this.#trumpBlock = el('div', { className: 'hud-trump', hidden: true }, [
      el('div', { className: 'hud-trump-labels' }, [
        el('span', { className: 'hud-caption', text: 'BRISCOLA' }),
        this.#deckLeftText,
        this.#trumpSeed,
      ]),
      this.#deckSlot,
    ]);

    this.#seatLayer = el('div', { className: 'seat-layer' });
    this.#cardLayer = el('div', { className: 'card-layer' });
    this.#emblem = feltEmblem();
    this.#description = el('p', { className: 'table-description' });
    this.#results = el('div', { className: 'results', hidden: true });
    // The chips of a question, on the cloth where the trick would be (choicePanelPlace)
    this.#choicePanel = el('div', { className: 'choice-panel', hidden: true });
    this.#felt = el('div', { className: 'felt' }, [
      this.#emblem,
      this.#seatLayer,
      this.#cardLayer,
      this.#choicePanel,
      this.#description,
      this.#results,
    ]);
    // Fixed over the whole page and never in the way of a tap: a card flies between two points
    // that may well be on opposite sides of the table, and one of them is often in the hand,
    // which is below the felt entirely
    this.#flightLayer = el('div', { className: 'flight-layer' });

    this.#hand = el('div', { className: 'hand' });
    this.#confirmButton = el('button', {
      className: 'button button-gold confirm-button',
      type: 'button',
      text: 'Conferma',
      disabled: true,
      onclick: () => this.#confirm(),
    });

    this.#root = el('div', { className: 'table' }, [
      this.#banner,
      el('div', { className: 'hud-bar' }, [
        el('div', { className: 'hud-left' }, [
          this.#matchIndicator,
          this.#declarationsInfo,
          this.#callInfo,
        ]),
        el('button', {
          className: 'pill-felt leave-button',
          type: 'button',
          text: 'Esci',
          onclick: () => this.#handlers.onLeave(),
        }),
        this.#trumpBlock,
      ]),
      this.#felt,
      el('div', { className: 'hand-wrapper' }, [this.#hand]),
      this.#confirmButton,
      this.#flightLayer,
    ]);
  }

  // The deck a room plays with is known while it is still filling up, and the artwork travels
  // over the network: fetched here it is in the cache long before the first card is dealt, fetched
  // at the deal it arrives after the card has already crossed the table. Harmless to call again
  // with the same deck, and attach() calls it too for the reconnection that skips the lobby
  prepare(deckType) {
    warmDeck(deckType);
  }

  attach(state) {
    this.#state = state;
    // Normally the lobby has already warmed this deck through prepare(). A reconnection has no
    // lobby before it, and a deal is about to land, so it is asked for again here
    warmDeck(state.deckType);
    this.#selectedHandIndex = -1;
    this.#selectedOption = -1;
    this.#generation += 1;
    this.#cancelTimers();
    clear(this.#seatLayer);
    clear(this.#cardLayer);
    clear(this.#flightLayer);
    clear(this.#hand);
    this.#seatNodes = new Map();
    this.#localSeatNode = null;
    this.#played = [];
    this.#playedKeys = [];
    this.#collected = false;
    this.#handKey = null;
    this.#handNodes = [];
    this.#dealtMatch = -1;
    this.#handCounts = state.players.map(() => 0);
    this.#playOrigin = null;
    if (!this.#resizeBound) {
      // Rotating a phone changes both how much overlap the hand needs and where everyone sits
      window.addEventListener('resize', () => this.#relayout());
      this.#observeFelt();
      this.#resizeBound = true;
    }
    this.render();
  }

  // The felt grows and shrinks on its own as the picker row appears and the banner comes and goes,
  // and none of that goes through render()
  #observeFelt() {
    if (typeof ResizeObserver !== 'function') return;
    new ResizeObserver(() => this.#relayout()).observe(this.#felt);
  }

  // Shown while the server replays our history after a reconnection: the table is being rebuilt,
  // and a half drawn one would look like a bug
  setRebuilding(rebuilding) {
    this.#silent = rebuilding;
    this.#banner.hidden = !rebuilding;
    this.#banner.textContent = rebuilding ? 'Ricostruzione della partita in corso...' : '';
  }

  render() {
    const state = this.#state;
    if (!state) return;
    this.#renderHud(state);
    this.#renderSeats(state);
    const landed = this.#syncPlayedCards(state);
    const dealt = this.#renderHand(state);
    this.#renderChoice(state);
    this.#renderResults(state);
    this.#description.textContent = state.description;
    this.#layout();
    this.#animate(state, landed, dealt);
  }

  /** The status panel above the table **/

  #renderHud(state) {
    this.#matchIndicator.textContent =
      state.matchesNumber > 0 ? `Mano ${state.currentMatch}/${state.matchesNumber}` : '';

    // The Marz game: the tricks declared so far against the tricks there are, updated at every
    // bet. Before the first one the line is invisible but still there, as on Android: taking it
    // out would shrink the panel at every new hand and stretch the felt under it
    const declared = state.declaredTotal();
    this.#declarationsInfo.hidden = !state.countsTricks;
    this.#declarationsInfo.style.visibility = declared === null ? 'hidden' : '';
    this.#declarationsInfo.textContent = `Dichiarazioni: ${declared ?? 0}/${state.dealtPerHand}`;

    const called = state.calledCards
      .map((card) => state.vocabulary.cardName(card, state.deckType))
      .join(', ');
    this.#callInfo.hidden = state.callerPosition < 0;
    this.#callInfo.textContent =
      state.callerPosition < 0
        ? ''
        : `${state.nameOf(state.callerPosition)} chiama ${called} a ${state.requiredPoints}`;

    // No trump at all (Tressette, Hearts, la vecia): the whole corner that shows one stays away
    const hasTrump = Boolean(state.briscolaCard || state.briscolaSeed);
    this.#trumpBlock.hidden = !hasTrump;
    // In the chiamata the deck is dealt out whole, so there is nothing left to draw and no count
    this.#deckLeftText.hidden = state.deckLeft <= 0;
    this.#deckLeftText.textContent = state.deckLeft > 0 ? `${state.deckLeft} nel mazzo` : '';
    // Briscola chiamata turns no card up: the trump is named in the auction instead
    this.#trumpSeed.hidden = Boolean(state.briscolaCard) || !state.briscolaSeed;
    this.#trumpSeed.textContent = state.briscolaSeed
      ? state.vocabulary.seedName(state.briscolaSeed, state.deckType)
      : '';

    clear(this.#deckSlot);
    if (state.briscolaCard) {
      const trump = this.#cardFace(state, state.briscolaCard, 'card card-trump');
      // Through a variable, so the side panel of a phone on its side can take it down further
      trump.style.setProperty(
        '--trump-width',
        `${cardWidthFor(state.deckType, TrumpCardWidth, TrumpCardMaxHeight)}px`,
      );
      this.#deckSlot.append(trump);
    }
  }

  /** The seats around the felt **/

  #renderSeats(state) {
    // A crowded table cannot carry a seat as wide as a name and a running total: around the rim of
    // a phone, six of those overlap outright. They shed the total, which the end of a hand shows
    // in full anyway, and the arithmetic that places them gets seats it can fit
    const compact = state.players.length - 1 >= CompactSeatsFrom;
    this.#felt.classList.toggle('seats-compact', compact);
    for (let position = 0; position < state.players.length; position += 1) {
      const local = position === state.myPosition;
      let seat = local ? this.#localSeatNode : this.#seatNodes.get(position);
      if (!seat) {
        seat = this.#buildSeat(state, position, local);
        this.#seatLayer.append(seat.node);
        if (local) this.#localSeatNode = seat;
        else this.#seatNodes.set(position, seat);
      }
      const gone = state.deletedPositions.includes(position);
      seat.node.classList.toggle('seat-turn', position === state.turn);
      seat.node.classList.toggle('seat-gone', gone);
      seat.statusView.textContent = this.#seatStatus(state, position, gone, compact);
      const notes = this.#seatNotes(state, position);
      seat.notesView.hidden = !notes;
      seat.notesView.textContent = notes;
    }
  }

  #buildSeat(state, position, local) {
    const nameView = el('span', { className: 'seat-name', text: state.nameOf(position) });
    const statusView = el('span', { className: 'seat-status' });
    const notesView = el('span', { className: 'seat-notes', hidden: true });
    const node = el('div', { className: local ? 'seat seat-local' : 'seat' }, [
      el('span', { className: 'avatar' }, [
        el('span', { className: 'avatar-initials', text: initialsOf(state.nameOf(position)) }),
      ]),
      nameView,
      statusView,
      notesView,
    ]);
    return { node, nameView, statusView, notesView, position };
  }

  // SeatView.refresh, with the status lines the Android fragments write into it: the games that
  // bet on tricks show tricks against bet, the ones that count card points show the points
  #seatStatus(state, position, gone, compact) {
    if (gone) return 'uscito';
    if (state.loserPosition === position) return 'ha la vecia';
    if (state.outPositions.includes(position)) return 'in salvo';
    const total = state.totals[position] ?? 0;
    // What a seat is counting towards its declaration is TRICKS, not the points in them: the Marz
    // game sends no points with a trick at all, and reading them left every seat on "0/3"
    const bet = state.bets[position];
    if (bet !== null && bet !== undefined) {
      const taken = state.tricksWon[position] ?? 0;
      return compact ? `${taken}/${bet}` : `${taken}/${bet} · ${total} pt`;
    }
    // La vecia keeps no score: what matters is how many cards are still in a hand
    if (state.gameType === 'VECIA') {
      const count = state.handCount(position);
      return count === 1 ? '1 carta' : `${count} carte`;
    }
    // Before a hand is declared there is nothing to count towards yet, and the points of a game
    // scored on tricks are zero all hand long: what a seat has to show is where it stands
    if (state.countsTricks) {
      return `${total} pt`;
    }
    // Everywhere else it is the points in the tricks that count, not how many there were
    const points = state.matchPoints[position] ?? 0;
    const side = this.#sideLabel(state, position);
    return compact
      ? `${state.formatPoints(points)}${side}`
      : `${state.formatPoints(points)} · ${total} tot${side}`;
  }

  // Which side a seat is on, as far as the local player is allowed to know it. In Briscola
  // chiamata that is deliberately almost nothing until the called card comes out
  #sideLabel(state, position) {
    const teams = state.teams;
    if (Array.isArray(teams) && teams.length > position && teams.length > state.myPosition) {
      // Only worth saying when there really are two sides: an odd table is everybody for
      // themselves, and heads up "us and them" says nothing
      if (new Set(teams).size === 2 && state.players.length > 2) {
        return teams[position] === teams[state.myPosition] ? ' · noi' : ' · loro';
      }
      return '';
    }
    if (state.callerPosition < 0) return '';
    if (position === state.callerPosition) return ' · chiama';
    if (state.partnerPositions.includes(position)) return ' · socio';
    return '';
  }

  // The extra line a game wants kept under a player's eyes for the whole hand: Tressette's accusi,
  // which are worth points and say something about what that player is holding, and in the
  // chiamata who called and who turned out to be their partner
  #seatNotes(state, position) {
    const notes = [...(state.declarations[position] ?? [])];
    if (position === state.callerPosition) notes.push('chiama');
    else if (state.partnerPositions.includes(position)) notes.push('socio');
    return notes.join(' · ');
  }

  /** The cards on the felt **/

  // state.trick only ever grows until the trick is over and a new card starts the next one, so a
  // shorter list, or one that no longer starts the same way, means the trick was replaced and the
  // felt has to be cleared. Answers with the cards that have just landed, for the flight to follow
  #syncPlayedCards(state) {
    const keys = state.trick.map(
      ({ position, card }) => `${position}:${card.seed}:${card.cardType}`,
    );
    const continues =
      keys.length >= this.#playedKeys.length &&
      this.#playedKeys.every((key, index) => key === keys[index]);
    if (!continues) {
      this.#clearPlayedCards();
    }
    // Once the winner has scooped the trick up the cards are gone for good, even though the state
    // still holds them until the first card of the next trick arrives
    if (this.#collected) {
      this.#playedKeys = keys;
      return [];
    }
    const landed = [];
    for (let index = this.#played.length; index < state.trick.length; index += 1) {
      const { position, card } = state.trick[index];
      const node = this.#cardFace(state, card, 'card played-card');
      this.#cardLayer.append(node);
      const entry = { position, card, node };
      this.#played.push(entry);
      landed.push(entry);
    }
    this.#playedKeys = keys;
    return landed;
  }

  #clearPlayedCards() {
    for (const { node } of this.#played) node.remove();
    this.#played = [];
    this.#playedKeys = [];
    this.#collected = false;
  }

  /** Where everything goes **/

  #relayout() {
    this.#layout();
    this.#fitHand();
  }

  // FeltTableLayout.onLayout: the seats on their arc, and every played card in front of its owner
  #layout() {
    const width = this.#felt.clientWidth;
    const height = this.#felt.clientHeight;
    if (!width || !height) return;

    const positions = seatOrder(this.#state.players.length, this.#state.myPosition).filter(
      (position) => this.#seatNodes.has(position),
    );
    const seatBoxes = positions.map((position) => boxOf(this.#seatNodes.get(position).node));
    const localBox = this.#localSeatNode ? boxOf(this.#localSeatNode.node) : null;

    // How big a played card is depends on the table it is played on, and where every card goes
    // depends on how big it is, so its size is settled before anything is placed
    const cardBox = this.#playedCardBox(width, height, seatBoxes, localBox);
    this.#cardWidth = cardBox.width;
    for (const entry of this.#played) entry.node.style.width = `${cardBox.width}px`;

    const geometry = tableGeometry({
      width,
      height,
      seats: seatBoxes,
      local: localBox,
      card: cardBox,
    });

    positions.forEach((position, index) => {
      place(this.#seatNodes.get(position).node, geometry.seats[index]);
    });
    if (this.#localSeatNode && geometry.local) place(this.#localSeatNode.node, geometry.local);

    for (const entry of this.#played) {
      const index = positions.indexOf(entry.position);
      place(entry.node, index < 0 ? geometry.localCard : geometry.cards[index]);
    }

    // FeltSurface.drawCenterEmblem: the crest is painted where the trick gathers, so the cards of
    // a hand under way land on it rather than beside it
    const size = Math.min(width, height) * 0.13 * 2.4;
    this.#emblem.style.width = `${size}px`;
    this.#emblem.style.height = `${size}px`;
    place(this.#emblem, geometry.trickCenter);
    // On its side the local player sits in the bottom left corner, where the status line would
    // otherwise run straight through their name
    this.#description.style.paddingLeft = geometry.localColumn ? `${geometry.localColumn}px` : '';
    this.#placeChoicePanel(width, height, geometry, seatBoxes, localBox);
  }

  #placeChoicePanel(width, height, geometry, seatBoxes, localBox) {
    const chips = [...this.#choicePanel.children];
    if (this.#choicePanel.hidden || chips.length === 0) return;
    const fit = () =>
      choicePanelPlace({
        width,
        height,
        geometry,
        seats: seatBoxes,
        local: localBox,
        // Measured to the fraction and rounded up: offsetWidth drops the half pixel of a chip
        // 41.5px wide, and a row worked out with the rounded figure did not fit its panel
        chips: chips.map((node) => {
          const box = node.getBoundingClientRect();
          return { width: Math.ceil(box.width) + ChipSlack, height: Math.ceil(box.height) };
        }),
      });
    this.#choicePanel.classList.remove('choice-panel-compact');
    let panel = fit();
    // Eight at the table on a short phone, or a vecia with twenty cards to draw from: smaller
    // chips before a panel that scrolls, whose last row would sit half hidden under its edge
    if (!panel.fits) {
      this.#choicePanel.classList.add('choice-panel-compact');
      panel = fit();
    }
    this.#choicePanel.style.width = `${panel.width}px`;
    this.#choicePanel.style.maxHeight = `${panel.height}px`;
    place(this.#choicePanel, panel);
  }

  #playedCardBox(feltWidth, feltHeight, seatBoxes, localBox) {
    const deckType = this.#state.deckType;
    const aspect = aspectFor(deckType);
    const target = Math.min(
      PlayedCardMaxWidth,
      Math.max(PlayedCardMinWidth, feltWidth * PlayedCardWidthShare),
    );
    // On its side the table lays the trick out in a row under the seats, and the strip it lies in
    // already says how tall a card can be
    if (isWideTable(feltWidth, feltHeight)) {
      const cardWidth = cardWidthFor(
        deckType,
        target,
        wideCardMaxHeight({ height: feltHeight, seats: seatBoxes, local: localBox }),
      );
      return { width: cardWidth, height: Math.round(cardWidth / aspect) };
    }
    const smallest = cardWidthFor(deckType, target, feltHeight * PlayedCardMaxHeightShare);
    const largest = cardWidthFor(
      deckType,
      Math.min(PlayedCardLargestWidth, feltWidth * PlayedCardLargestWidthShare),
      feltHeight * PlayedCardLargestHeightShare,
    );
    return fitPlayedCard({
      width: feltWidth,
      height: feltHeight,
      seats: seatBoxes,
      local: localBox,
      aspect,
      smallest,
      largest: Math.max(smallest, largest),
    });
  }

  /** Everything that moves **/

  #animate(state, landed, dealt) {
    if (this.#silent) {
      // A replay is not a game being watched: the table is simply put back the way it was
      this.#dealtMatch = state.currentMatch;
      this.#rememberHandCounts(state);
      for (const node of dealt) node.classList.remove('card-dealing');
      this.#playOrigin = null;
      return;
    }

    const newDeal = state.currentMatch !== this.#dealtMatch && state.myHand.length > 0;
    this.#dealtMatch = state.currentMatch;
    if (newDeal) {
      this.#dealCards(state, dealt);
    } else {
      for (const node of dealt) node.classList.remove('card-dealing');
      this.#drawCards(state);
    }
    this.#rememberHandCounts(state);

    for (const entry of landed) this.#flyPlayedCard(state, entry);
    this.#collectTrick(state);
  }

  #rememberHandCounts(state) {
    this.#handCounts = state.players.map((unused, position) => state.handCount(position));
  }

  #takePlayOrigin() {
    const origin = this.#playOrigin;
    this.#playOrigin = null;
    return origin;
  }

  // TrickGameFragment.showPlayedCard: the card is put in front of its owner and then flown there,
  // out of the hand for the local player and off the seat itself for everybody else
  #flyPlayedCard(state, entry) {
    const to = rectOf(entry.node);
    if (!to) return;
    const mine = entry.position === state.myPosition;
    // Consumed rather than merely read: the answer is sent from a render of its own, and the echo
    // that actually puts the card on the felt is a render or two later. Clearing the origin on
    // every render, as this used to, meant the local player's own card was the one card at the
    // table that never flew anywhere
    const origin = mine ? this.#takePlayOrigin() : rectOf(this.#seatNodes.get(entry.position)?.node);
    if (!origin) return;
    entry.node.classList.add('card-arriving');
    this.#fly({
      from: centerAt(origin, to.width, to.height),
      to: { x: to.left, y: to.top },
      width: to.width,
      height: to.height,
      card: entry.card,
      faceUp: true,
      deckType: state.deckType,
      onEnd: () => entry.node.classList.remove('card-arriving'),
    });
  }

  // FeltTableLayout.collectPlayedCards: the trick slides over to whoever took it, shrinking and
  // fading, so it reads as them scooping the cards up
  #collectTrick(state) {
    if (!state.trickComplete || this.#collected || this.#played.length === 0) return;
    this.#collected = true;
    const winner = state.lastTrickWinner;
    // Off the books straight away: from here on these cards belong to the animation, and the next
    // render must not try to keep laying them out
    const cards = this.#played;
    this.#played = [];
    this.#after(CollectDelayMillis, () => {
      const seat = winner === state.myPosition ? this.#localSeatNode : this.#seatNodes.get(winner);
      const target = rectOf(seat?.node) ?? rectOf(this.#felt);
      for (const { node } of cards) {
        const from = rectOf(node);
        if (!from || !target || typeof node.animate !== 'function') {
          node.remove();
          continue;
        }
        const to = centerAt(target, from.width, from.height);
        const dx = to.x - from.left;
        const dy = to.y - from.top;
        // The card is already centred on its spot with a translate of its own, so the slide is
        // written on top of that instead of replacing it
        const collect = node.animate(
          [
            { transform: 'translate(-50%, -50%)', opacity: 1 },
            {
              transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(0.35)`,
              opacity: 0,
            },
          ],
          { duration: CollectMillis, easing: 'ease-in', fill: 'forwards' },
        );
        this.#flights.add(collect);
        const done = () => {
          this.#flights.delete(collect);
          node.remove();
        };
        collect.finished.then(done, done);
      }
    });
  }

  // TrickGameFragment.animateDeal: the hand is laid out but kept invisible, and the cards fly in
  // from the deck one after the other to reveal themselves. The opponents get face down ones
  #dealCards(state, dealt) {
    const deck = this.#deckOrigin();
    dealt.forEach((node, index) => {
      const to = rectOf(node);
      if (!to || !deck) {
        node.classList.remove('card-dealing');
        return;
      }
      this.#fly({
        from: centerAt(deck, to.width, to.height),
        to: { x: to.left, y: to.top },
        width: to.width,
        height: to.height,
        card: state.myHand[this.#handNodes.indexOf(node)] ?? null,
        faceUp: true,
        deckType: state.deckType,
        delay: index * DealStaggerMillis,
        onEnd: () => node.classList.remove('card-dealing'),
      });
    });
    // Decorative, but it is what makes the deal read as a deal
    for (const [position, seat] of this.#seatNodes) {
      this.#flyBacksTo(
        state,
        seat.node,
        Math.min(state.handCount(position), MaxDealtBacksPerSeat),
        deck,
      );
    }
  }

  // TrickGameFragment.animateDraw: in Briscola a card is drawn after every trick, and without the
  // flight the hand simply refills itself out of nowhere
  #drawCards(state) {
    const deck = this.#deckOrigin();
    for (const [position, seat] of this.#seatNodes) {
      const drawn = state.handCount(position) - (this.#handCounts[position] ?? 0);
      if (drawn > 0) this.#flyBacksTo(state, seat.node, drawn, deck);
    }
    const mine = state.myHand.length - (this.#handCounts[state.myPosition] ?? 0);
    if (mine <= 0) return;
    const first = Math.max(0, this.#handNodes.length - mine);
    for (let index = first; index < this.#handNodes.length; index += 1) {
      const node = this.#handNodes[index];
      const to = rectOf(node);
      if (!to || !deck) continue;
      node.classList.add('card-dealing');
      this.#fly({
        from: centerAt(deck, to.width, to.height),
        to: { x: to.left, y: to.top },
        width: to.width,
        height: to.height,
        card: state.myHand[index],
        faceUp: true,
        deckType: state.deckType,
        delay: (index - first) * DealStaggerMillis,
        onEnd: () => node.classList.remove('card-dealing'),
      });
    }
  }

  #flyBacksTo(state, node, count, deck) {
    const target = rectOf(node);
    if (!target || !deck || count <= 0) return;
    // A card nobody gets to see is still a card of this table: the same size as the played ones
    const width = this.#cardWidth;
    const height = Math.round(width / aspectFor(state.deckType));
    for (let index = 0; index < count; index += 1) {
      this.#fly({
        from: centerAt(deck, width, height),
        to: centerAt(target, width, height),
        width,
        height,
        card: null,
        faceUp: false,
        deckType: state.deckType,
        delay: index * DealStaggerMillis,
      });
    }
  }

  // Where a dealt card comes from: the deck itself when the game shows one, and the middle of the
  // table when it does not, which is where the dealer's hands would be
  #deckOrigin() {
    const deck = rectOf(this.#deckSlot);
    return deck && deck.width > 0 ? deck : rectOf(this.#felt);
  }

  // CardFlightOverlay.fly: a throwaway copy of the card travels between two points on a layer that
  // never takes a tap, and the real card takes over the moment it lands.
  //
  // An animation and not a CSS transition. A transition only runs if the browser has worked out
  // the starting style before the end one is set, and a copy appended and sent off within the same
  // frame never had one: it was drawn straight on its landing spot. Sampled frame by frame, a card
  // played by somebody else sat there for the whole flight and never moved; the deal only half
  // flew, because measuring each card forced the style of the one before. An animation states both
  // of its ends, and `finished` is when the card has really landed rather than when a timer started
  // alongside it reckoned it would have
  #fly({ from, to, width, height, card, faceUp, deckType, delay = 0, onEnd }) {
    const node = this.#cardFace({ deckType }, faceUp ? card : null, 'card flying-card');
    if (typeof node.animate !== 'function') {
      onEnd?.();
      return;
    }
    const generation = this.#generation;
    node.style.width = `${width}px`;
    node.style.height = `${height}px`;
    node.style.aspectRatio = 'auto';
    // A card waiting its turn in a deal is not on the table yet: it appears the instant it leaves
    node.style.opacity = '0';
    this.#flightLayer.append(node);
    const flight = node.animate(
      [
        { transform: `translate(${from.x}px, ${from.y}px)`, opacity: 1 },
        { transform: `translate(${to.x}px, ${to.y}px)`, opacity: 1 },
      ],
      { duration: FlightMillis, delay, easing: 'ease-out', fill: 'forwards' },
    );
    this.#flights.add(flight);
    flight.finished.then(
      () => {
        this.#flights.delete(flight);
        // Both in the same frame: the real card shows up exactly where its copy disappears
        if (generation === this.#generation) onEnd?.();
        node.remove();
      },
      // Cancelled because the table was thrown away
      () => node.remove(),
    );
  }

  #after(millis, action) {
    const timer = setTimeout(() => {
      this.#timers.delete(timer);
      action();
    }, millis);
    this.#timers.add(timer);
  }

  #cancelTimers() {
    for (const timer of this.#timers) clearTimeout(timer);
    this.#timers.clear();
    for (const flight of this.#flights) flight.cancel();
    this.#flights.clear();
  }

  /** The player's own hand and the answer they owe the engine **/

  // Rebuilt only when the cards themselves change: a flight in progress needs the card it is
  // revealing to still be the same node when it lands, and a re-render for a status line would
  // otherwise pull it out from under it. Answers with the nodes that are new, for the deal
  #renderHand(state) {
    const choosingCard = state.choice.kind === Choice.CARD;
    const key = state.myHand.map((card) => `${card.seed}:${card.cardType}`).join('|');
    let dealt = [];
    if (key !== this.#handKey) {
      const before = this.#handKey === null ? -1 : this.#handNodes.length;
      clear(this.#hand);
      this.#handNodes = state.myHand.map((card, index) => {
        const node = this.#cardFace(state, card, 'card hand-card');
        node.addEventListener('click', () => this.#pickCard(index));
        this.#hand.append(node);
        return node;
      });
      // Only a hand that grew was dealt to: one that shrank just had a card played out of it
      if (before >= 0 && this.#handNodes.length > before) {
        dealt = this.#handNodes.slice(before);
        for (const node of dealt) node.classList.add('card-dealing');
      }
      this.#handKey = key;
    }
    this.#handNodes.forEach((node, index) => {
      const playable = choosingCard && state.isPlayable(state.myHand[index]);
      node.classList.toggle('playable', playable);
      node.classList.toggle('dimmed', choosingCard && !playable);
      node.classList.toggle('selected', index === this.#selectedHandIndex);
    });

    const total = state.totals[state.myPosition] ?? 0;
    this.#hand.setAttribute('data-score', `totale ${state.formatPoints(total)}`);
    this.#fitHand();
    return dealt;
  }

  // Thirteen cards of the french deck do not fit a phone side by side, and letting them wrap turns
  // the hand into three or four stacked rows. Overlap them instead, by as little as the width
  // actually requires: a short hand keeps a normal gap and nothing is hidden
  #fitHand() {
    const cards = this.#hand.children;
    const available = this.#hand.clientWidth;
    // Not offsetWidth: a trevisana 118px tall is 48.5px wide, and the half pixel offsetWidth drops,
    // ten times over, pushed the last card off the edge
    const cardWidth = cards[0]?.getBoundingClientRect().width ?? 0;
    this.#hand.style.setProperty('--hand-gap', `${handGap(available, cardWidth, cards.length)}px`);
  }

  #renderChoice(state) {
    const kind = state.choice.kind;
    const picking =
      kind === Choice.BID || kind === Choice.RANK || kind === Choice.SEED || kind === Choice.DRAW;
    // On the felt and over it, never beside it: coming and going, it moves nothing. It used to be a
    // band under the felt, reserved for the whole game so that the felt would not shrink the
    // moment a hand was declared, and the Marz game paid 56px of cloth for chips it shows a few
    // seconds a hand. Where it goes is worked out with the seats, in #layout
    this.#choicePanel.hidden = !picking;
    clear(this.#choicePanel);
    if (picking) {
      for (const option of state.choice.options) {
        this.#choicePanel.append(
          el('button', {
            className: option.response === this.#selectedOption ? 'chip selected' : 'chip',
            type: 'button',
            text: option.label,
            onclick: () => this.#pickOption(option.response),
          }),
        );
      }
    }
    // Never taken out of the layout, only greyed: hiding it moved the whole table by its own
    // height plus a gap every single time a turn came round or was answered
    if (state.finished) {
      this.#confirmButton.textContent = 'Torna alla lobby';
      this.#confirmButton.disabled = false;
      return;
    }
    this.#confirmButton.textContent = 'Conferma';
    this.#confirmButton.disabled =
      kind === Choice.CARD ? this.#selectedHandIndex < 0 : this.#selectedOption < 0;
  }

  #renderResults(state) {
    this.#results.hidden = !state.results;
    if (!state.results) return;
    clear(this.#results);
    this.#results.append(
      el('h3', {
        text: state.results.final ? 'Risultato finale' : `Fine mano ${state.currentMatch}`,
      }),
    );
    const ordered = [...state.results.rows].sort((a, b) => b.total - a.total);
    for (const row of ordered) {
      this.#results.append(
        el(
          'div',
          { className: row.position === state.myPosition ? 'result-row mine' : 'result-row' },
          [
            el('span', { text: row.name }),
            el('span', {
              text: `${state.formatPoints(row.total)}${row.gain ? ` (+${state.formatPoints(row.gain)})` : ''}`,
            }),
          ],
        ),
      );
    }
    // TrickGameFragment holds its notification queue on the end of hand overlay until "Continua" is
    // pressed: a score is read, not watched, and the next deal would wipe it off the felt within a
    // second. The final one has the leave button under it instead and nothing left to continue to
    if (!state.results.final) {
      this.#results.append(
        el('button', {
          className: 'button button-gold results-continue',
          type: 'button',
          text: 'Continua',
          onclick: () => this.#handlers.onContinue?.(),
        }),
      );
    }
  }

  // `state` here is only ever asked for its deck and its vocabulary, so a flying copy can be built
  // from the deck type alone
  #cardFace(state, card, className) {
    const ratio = aspectFor(state.deckType);
    const node = el('div', { className });
    node.style.aspectRatio = String(ratio);
    // For the corners, which are rounded by a share of the card's width (see .card)
    node.style.setProperty('--card-aspect', String(ratio));
    if (!card) {
      const backUrl = cardBackUrl(state.deckType);
      if (backUrl) {
        node.append(el('img', { src: backUrl, alt: 'Carta coperta', draggable: 'false' }));
        return node;
      }
      node.classList.add('card-back');
      // The inner frame of the drawn back is inset by a tenth of the card's WIDTH on every side,
      // so the vertical share depends on how narrow the deck is
      node.style.setProperty('--back-inset', `${(10 * ratio).toFixed(2)}%`);
      return node;
    }
    const url = cardImageUrl(state.deckType, card.seed, card.cardType);
    const name = state.vocabulary ? state.vocabulary.cardName(card, state.deckType) : 'Carta';
    if (url) {
      // Not lazy: a card is on screen the instant it exists, and deferring the fetch is exactly how
      // it ends up flying as a white rectangle. No decoding hint either - 'sync' buys the first
      // frame at the price of decoding on the main thread, which is the thread the flight runs on
      node.append(el('img', { src: url, alt: name, draggable: 'false' }));
    } else {
      // No artwork for this card in this deck: its name is still better than a blank
      node.append(el('span', { className: 'card-text', text: name }));
    }
    node.title = name;
    return node;
  }

  #pickCard(index) {
    const state = this.#state;
    if (!state || state.choice.kind !== Choice.CARD || !state.isPlayable(state.myHand[index])) {
      return;
    }
    // Only the confirm button plays a card. Tapping the selected one again used to play it, and a
    // double tap meant to look at a card closely sent it to the table instead
    if (this.#selectedHandIndex === index) return;
    this.#selectedHandIndex = index;
    this.render();
  }

  #pickOption(index) {
    this.#selectedOption = index;
    this.render();
  }

  #confirm() {
    const state = this.#state;
    if (!state) return;
    if (state.finished) {
      this.#handlers.onLeave();
      return;
    }
    const kind = state.choice.kind;
    let response = -1;
    if (kind === Choice.CARD) {
      response = state.responseForHandIndex(this.#selectedHandIndex);
      // Where the card sits right now, so the flight the server triggers a moment later starts
      // from the player's own hand and not from the middle of nowhere
      this.#playOrigin = rectOf(this.#handNodes[this.#selectedHandIndex]);
    } else if (kind !== Choice.NONE) {
      response = this.#selectedOption;
    }
    if (response < 0) return;

    // Clear locally straight away: the answer can only be sent once, and the echo from the server
    // is what actually retires the choice
    this.#selectedHandIndex = -1;
    this.#selectedOption = -1;
    state.clearChoice();
    state.description = 'In attesa degli altri giocatori...';
    this.render();
    this.#handlers.onRespond(response);
  }
}

// FeltSurface.drawCenterEmblem: two rings and four diamonds, barely there. Drawn as an SVG rather
// than with borders because the diamonds sit on the ring at the cardinal points, and a stack of
// rotated boxes to say that would be worse than the dozen lines it takes
function feltEmblem() {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('class', 'felt-emblem');
  svg.setAttribute('viewBox', '-120 -120 240 240');
  const ring = (radius, width) => {
    const node = document.createElementNS(ns, 'circle');
    node.setAttribute('r', String(radius));
    node.setAttribute('fill', 'none');
    node.setAttribute('stroke-width', String(width));
    return node;
  };
  svg.append(ring(100, 3.5), ring(78, 1.8));
  for (let quarter = 0; quarter < 4; quarter += 1) {
    const angle = (quarter * Math.PI) / 2;
    const x = Math.cos(angle) * 100;
    const y = Math.sin(angle) * 100;
    const diamond = document.createElementNS(ns, 'rect');
    diamond.setAttribute('x', String(x - 16));
    diamond.setAttribute('y', String(y - 16));
    diamond.setAttribute('width', '32');
    diamond.setAttribute('height', '32');
    diamond.setAttribute('class', 'felt-emblem-diamond');
    diamond.setAttribute('transform', `rotate(45 ${x} ${y})`);
    svg.append(diamond);
  }
  return svg;
}

// A laid out node's box, or a zero one before the browser has had a chance to measure it
function boxOf(node) {
  return { width: node.offsetWidth ?? 0, height: node.offsetHeight ?? 0 };
}

function rectOf(node) {
  if (!node || typeof node.getBoundingClientRect !== 'function') return null;
  const rect = node.getBoundingClientRect();
  return rect.width || rect.height ? rect : null;
}

// The top left corner a card of this size would need to sit centred on `rect`
function centerAt(rect, width, height) {
  return { x: rect.left + rect.width / 2 - width / 2, y: rect.top + rect.height / 2 - height / 2 };
}

function place(node, at) {
  node.style.left = `${at.x}px`;
  node.style.top = `${at.y}px`;
}
